// theme constant
import { IconAlertTriangle, IconAlertCircle, IconInfoCircle } from '@tabler/icons-react';
import dayjs from 'dayjs';
export const WEB_VERSION = '10.2.1.2';
export const gridSpacing = 3;
export const drawerWidth = 260;
export const appDrawerWidth = 320;

// Schedule constants (used by Mobile Profiles schedule drawer + other schedule UIs)
export const DAY_KEYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const DAY_NAMES_MON_FIRST = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const DAY_NAMES_SUN_FIRST = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const SCHEDULE_MAX_LEN = 32;

// Schedule helpers
export const parseHHmmToDayjsHour = (hhmm) => {
  if (!hhmm || typeof hhmm !== 'string') return null;
  const parts = hhmm.split(':');
  if (parts.length < 1) return null;
  const h = parseInt(parts[0], 10);
  if (Number.isNaN(h)) return null;
  return dayjs().hour(h).minute(0).second(0).millisecond(0);
};
export const deviceStatus = {
  status0: 'Offline',
  status1: 'Online',
  status2: 'Locally managed device',
  status3: 'Waiting for first connect',
  status4: 'Device is rebooting',
  status5: 'Still waiting to connect',
  status6: 'Device failed to connect back',
  status7: 'Switching to standalone mode',
  status8: 'Switching to cloud mode',
  status9: 'Standalone mode',
  status10: 'Configuration in progress',
  status11: 'Firmware is upgrading',
  status12: 'Checking device firmware',
  status13: 'Offline',
  status14: 'Waiting to connect',
  status17: 'Online (PoE 802.3af only)',
  status22: 'Got IP address',
  status21: 'Mesh setup is in progress',
  status28: 'Connected to Root',
  status27: 'Visible to the Root',
  status26: 'Waiting to be connected',
  status25: 'Waiting to be connected',
  status24: 'Error in communication',
  status31: 'Pending verification',
  status32: 'Verification failed',
  status41: 'Validation failed',
  status42: 'Partially Connected',
  status43: 'Managed by Engage controller',
  status33: 'Validation failed',
  status34: 'Online (PoE 802.3at only)'
};
export const deviceStatusColor = {
  0: 'error.main',
  1: 'success.dark',
  2: 'warning.dark',
  3: 'grey.500',
  4: 'warning.dark',
  5: 'grey.700',
  6: 'error.main',
  7: 'error.main',
  8: 'error.main',
  9: 'error.main',
  10: 'warning.dark',
  11: 'grey.700',
  12: 'grey.700',
  13: 'error.main',
  14: 'grey.500',
  31: 'grey.700',
  32: 'grey.700',
  24: 'grey.700',
  21: 'warning.dark',
  22: 'grey.700',
  25: 'grey.700',
  27: 'grey.700',
  28: 'success.dark',
  41: 'error.main',
  42: 'error.main',
  43: 'error.main',
  33: 'error.main', // showing the color red to the status validation failed for jira PRJCBUGEN-50794
  17: 'success.dark',
  34: 'success.dark',
  35: 'purple.300', // purple color for hyperlink etc...
  36: 'critical.main'
};

// Notification type constants and color utilities
export const NOTIFICATION_TYPES = {
  INFO: 'Info',
  CRITICAL: 'Critical',
  WARNING: 'Warning',
  ERROR: 'Error',
  ALERT: 'Alert'
};

// Background colors for notification type chips (used in NotificationList)
export const getTypeColor = (type, theme) => {
  const isDark = theme.palette.mode === 'dark';
  switch (type) {
    case NOTIFICATION_TYPES.INFO:
      return isDark ? 'info' : '#E3F2FD';
    case NOTIFICATION_TYPES.CRITICAL:
      return isDark ? '#26171a' : '#FFEBEE';
    case NOTIFICATION_TYPES.WARNING:
      return isDark ? '#32231b' : '#FFF8E1';
    case NOTIFICATION_TYPES.ERROR:
      return isDark ? '#26171a' : '#FFEBEE';
    default:
      return '#F5F5F5';
  }
};

// Text colors for notification type chips (used in NotificationList)
export const getTypeTextColor = (type, theme) => {
  const isDark = theme.palette.mode === 'dark';
  switch (type) {
    case NOTIFICATION_TYPES.INFO:
      return '#1976D2';
    case NOTIFICATION_TYPES.CRITICAL:
      return isDark ? '#a75656' : '#D32F2F';
    case NOTIFICATION_TYPES.WARNING:
      return '#F57F17';
    case NOTIFICATION_TYPES.ERROR:
      return isDark ? '#a75656' : '#D32F2F';
    default:
      return '#424242';
  }
};
// Icons for notification type chips (used in NotificationList)
export const getTypeIcons = (type, theme) => {
  const isDark = theme.palette.mode === 'dark';
  switch (type) {
    case NOTIFICATION_TYPES.INFO:
      return <IconInfoCircle size={20} stroke={1.5} color="#0288D1" />; // blue info
    case NOTIFICATION_TYPES.CRITICAL:
      return <IconAlertCircle size={20} stroke={1.5} color={isDark ? '#a75656' : '#D32F2F'} />; // red
    case NOTIFICATION_TYPES.WARNING:
      return <IconAlertTriangle size={18} stroke={1.5} color="#F57F17" />; // amber
    case NOTIFICATION_TYPES.ERROR:
      return <IconAlertCircle size={20} stroke={1.5} color={isDark ? '#a75656' : '#D32F2F'} />; // red
    default:
      return <IconInfoCircle size={20} stroke={1.5} color="#424242" />; // default to blue info;
  }
};

// Text colors for notification type in data grid (used in notifications page)
export const getTypeTextColorForGrid = (type) => {
  switch (type) {
    case NOTIFICATION_TYPES.INFO:
      return '#0288D1';
    case NOTIFICATION_TYPES.WARNING:
      return '#FF9800';
    case NOTIFICATION_TYPES.CRITICAL:
      return '#D32F2F';
    case NOTIFICATION_TYPES.ERROR:
      return '#D32F2F';
    default:
      return '#4D4D4D';
  }
};
// Color mapping object for easy access (NotificationList style)
export const NOTIFICATION_COLORS = {
  [NOTIFICATION_TYPES.INFO]: {
    background: '#E3F2FD',
    text: '#1976D2'
  },
  [NOTIFICATION_TYPES.CRITICAL]: {
    background: '#FFEBEE',
    text: '#D32F2F'
  },
  [NOTIFICATION_TYPES.WARNING]: {
    background: '#FFF8E1',
    text: '#F57F17'
  },
  [NOTIFICATION_TYPES.ERROR]: {
    background: '#FFEBEE',
    text: '#D32F2F'
  },
  default: {
    background: '#F5F5F5',
    text: '#424242'
  }
};

// Color mapping object for data grid (notifications page style)
export const NOTIFICATION_GRID_COLORS = {
  [NOTIFICATION_TYPES.INFO]: '#0288D1',
  [NOTIFICATION_TYPES.WARNING]: '#FF9800',
  [NOTIFICATION_TYPES.ALERT]: '#E65100',
  [NOTIFICATION_TYPES.ERROR]: '#E65100',
  default: '#424242'
};
// constant values for SSID settings
export const AUTHENTICATION_TYPES = [
  { 0: 'Open' },
  { 32: 'WPA2 Personal' },
  { 48: 'WPA2 Personal Mixed' },
  { 8: 'WPA2 Enterprise' },
  { 80: 'WPA3 Personal' },
  { 96: 'WPA3 Personal Mixed (WPA2 + WPA3)' },
  { 112: 'WPA3 Enterprise' },
  // 112 is Suite-B-192, 128 advertises WPA-EAP-SHA256, 144 advertises WPA-EAP + WPA-EAP-SHA256.
  { 128: 'WPA3 Enterprise Standard' },
  { 144: 'WPA3 Enterprise Mixed (WPA2 + WPA3)' }
];
// const date for schedule
export const DAY_SCHEDULE = [
  { status: '0', dayChar: 'm', startTime: ['00'], endTime: ['00'] },
  { status: '0', dayChar: 't', startTime: ['00'], endTime: ['00'] },
  { status: '0', dayChar: 'w', startTime: ['00'], endTime: ['00'] },
  { status: '0', dayChar: 't', startTime: ['00'], endTime: ['00'] },
  { status: '0', dayChar: 'f', startTime: ['00'], endTime: ['00'] },
  { status: '0', dayChar: 's', startTime: ['00'], endTime: ['00'] },
  { status: '0', dayChar: 's', startTime: ['00'], endTime: ['00'] }
];
// Options for the session timeout dropdown
export const SESSION_TIMEOUT_OPTION = [
  '5 min',
  '15 min',
  '30 min',
  '45 min',
  ...Array.from({ length: 24 }, (_, i) => i + 1 + ' hr'), // 1 hr to 24 hr
  '3 days',
  '5 days',
  '7 days',
  '1 month'
];
//External Captive Portal
export const DEFAULT_ECP_SERVICES = {
  ipAddress: '0.0.0.0',
  portOne: '1812',
  portTwo: '1813',
  authenticationKey: 'sharedsecret',
  webAuthServerURL: 'http://www.netgear.com'
};
// constants used for advance rate selection at ssid level
export const FIXED_ARS_VALUE = {
  editableResponse: ['15030', '7011', '30102', '7006', '7011', '8007', '30117', '30271', '30257', '20001'],
  nonEditableResponse: [
    '11090',
    '7075',
    '17000',
    '8021',
    '8005',
    '30101',
    '7096',
    '8068',
    '8003',
    '30108',
    '30104',
    '30105',
    '30111',
    '8008',
    '8020',
    '40018'
  ],
  densityLevelArs: ['0', '1', '2', '3', '4'],
  colors: ['#00AAFF', '#2C262D', '#00D76F', '#DB8E11', '#6E2585', '#FC3858', '#504B51', '#FFCA65', '#DA09BE', '#86D7FF']
};
// constant value for ars in ssid level
export const DEFAULT_ARS_VALUE = {
  wlan0: {
    fixedMulticastRateDef: '11',
    multicastRate: '11',
    fixedMulticastRateArray: [
      {
        code: '0',
        value: 'Auto'
      },
      {
        code: '1',
        value: '1 Mbps'
      },
      {
        code: '2',
        value: '2 Mbps'
      },
      {
        code: '5.5',
        value: '5.5 Mbps'
      },
      {
        code: '11',
        value: '11 Mbps'
      }
    ],
    densityLevel: '0'
  },
  wlan1: {
    fixedMulticastRateDef: '24',
    multicastRate: '24',
    fixedMulticastRateArray: [
      {
        code: '0',
        value: 'Auto'
      },
      {
        code: '6',
        value: '6 Mbps'
      },
      {
        code: '12',
        value: '12 Mbps'
      },
      {
        code: '24',
        value: '24 Mbps'
      },
      {
        code: '54',
        value: '54 Mbps'
      }
    ],
    densityLevel: '0'
  },
  wlan2: {
    fixedMulticastRateDef: '24',
    multicastRate: '24',
    fixedMulticastRateArray: [
      {
        code: '0',
        value: 'Auto'
      },
      {
        code: '6',
        value: '6 Mbps'
      },
      {
        code: '12',
        value: '12 Mbps'
      },
      {
        code: '24',
        value: '24 Mbps'
      },
      {
        code: '54',
        value: '54 Mbps'
      }
    ],
    densityLevel: '0'
  },
  wlan2_6: {
    fixedMulticastRateDef: '0',
    multicastRate: '0',
    fixedMulticastRateArray: [
      {
        code: '0',
        value: 'Auto'
      },
      {
        code: '6',
        value: '6 Mbps'
      },
      {
        code: '12',
        value: '12 Mbps'
      },
      {
        code: '24',
        value: '24 Mbps'
      }
    ],
    densityLevel: '0'
  }
};

export const DEFAULT_ADMIN_USER = 'admin';

// Default SSID data for add mode
// This function accepts parsed arsData and returns the complete default data structure
export const DEFAULT_RATE_LIMIT_INFO = {
  isRateLimitExceeded: false,
  rateEnabledSSIDNames: [],
  locationData: []
};

export const getDefaultSSIDData = (arsData) => {
  return {
    ssidName: '',
    securityValue: 32,
    mloStatus: false,
    showPassword: '',
    advanceSettings: '1',
    broadCastingSelection: '0',
    vlanDetails: { vlanID: '', vlanType: '1' },
    trafficSelected: '0',
    selectedVlanidType: '1',
    bandSelected: { bandall: false, band2: true, band5: true, band6: false },
    resultedBand: 8,
    securitySt: 0,
    mpskDetails: { mpskList: [], mpskGroup: [], isMPSKSupported: true, mpskSelectionLimit: '1000', enabled: false },
    wirelessRegion: 'US',
    startEndIP: { start: '', end: '' },
    subNet: '',
    nextSSIDNo: '',
    networkAddress: '',
    dns: '8.8.8.8',
    leaseTime: '1440',
    mDU: false,
    oweMode: '0',
    clientIsolation: false,
    clientAllowDomain: { status: false, domain: [] },
    clientLength: 0,
    bandSteering: false,
    fastRoming: false,
    kvData: false,
    scheduleEnabled: false,
    scheduleInput: { scheduleId: '', name: '' },
    ecpPayload: {},
    icpPayload: {},
    fbCpStatus: '0',
    logoFile: { businessLogo: null, bckgrdImage: null, advertImage: null },
    isCaptivePortalEnabled: false,
    macACLData: { macAclStatus: '2', macBlackAcl: [], macWhiteAcl: [], mode: 0 },
    clientOsBlockPolicy: { enabled: false, blockedOs: [] },
    dhcpOfferBcastToUcast: '1',
    arsData: arsData || {},
    ssidInfoData: {},
    selectedBand: 'wlan0',
    natMode: {},
    arsStatus: '0',
    rateLimit: {
      enabled: false,
      uploadRateLimit: '0',
      downloadRateLimit: '0'
    },
    rateLimitInfo: DEFAULT_RATE_LIMIT_INFO
  };
};
// constant value for the Rule Traffic Filters
export const addAclRule = {
  ruleName: '',
  state: '0',
  action: '1',
  direction: '2',
  layer: '0',
  protocol: '0',
  src: {
    type: 'ANY',
    address: '',
    port: '-'
  },
  dest: {
    type: 'ANY',
    address: '',
    port: '-'
  }
};
export const defaultProtocolType = ['ANY', 'ARP', 'TCP', 'UDP', 'ICMP', 'IGMP'];
export const layerType2 = ['ANY', 'MAC Address'];
export const layerType3 = ['ANY', 'IP Address', 'IP Subnet'];
export const alignProtocol = { 0: 'ANY', 1: 'ARP', 2: 'TCP', 3: 'UDP', 4: 'ICMP', 5: 'IGMP' };

// ==============================|| Notification Settings (User Profile) ||============================== //
// Frequency option labels are localized via these intl ids; index = backend value
export const FREQUENCY_OPTION_IDS = [
  'notiFreqImmediately',
  'notiFreq15Min',
  'notiFreq30Min',
  'notiFreq1Hour',
  'notiFreq2Hours',
  'notiFreq3Hours',
  'notiFreq6Hours'
];
// Offline duration option labels are localized via these intl ids; index = backend value
export const OFFLINE_DURATION_OPTION_IDS = [
  'notiOfflineImmediately',
  'notiOffline15Min',
  'notiOffline30Min',
  'notiOffline1Hour',
  'notiOffline2Hours',
  'notiOffline3Hours',
  'notiOffline6Hours'
];
// Default option indices exposed when the API has not yet returned a list
// (matches the legacy backend default arrays).
export const DEFAULT_FREQUENCY_INDICES = [0, 1, 2, 3, 4, 5, 6];
export const DEFAULT_OFFLINE_INDICES = [0, 1, 2, 3, 4, 5, 6];
// Tab identifiers for the Notification Settings page
export const NOTI_TABS = {
  GLOBAL: 'global',
  ORGANIZATION: 'organization',
  EVENT: 'event'
};
// Inner sub-tabs of the Event Notifications page (mirrors the legacy
// "Configure Notifications" panel: Account / Organization / Network / Device).
// The numeric `level` is the trailing path segment expected by the legacy
// notification API (`getAccNotiDetails(level)` / `postNotiConfigDetails`).
export const NOTI_EVENT_SUBTABS = {
  ACCOUNT: 'account',
  ORGANIZATION: 'organization',
  NETWORK: 'network',
  DEVICE: 'device'
};
export const NOTI_EVENT_SUBTAB_LEVELS = {
  [NOTI_EVENT_SUBTABS.ACCOUNT]: '1',
  [NOTI_EVENT_SUBTABS.ORGANIZATION]: '2',
  [NOTI_EVENT_SUBTABS.NETWORK]: '3',
  [NOTI_EVENT_SUBTABS.DEVICE]: '4'
};
// Severity legend used by every event-notification table.
// Severity ids match the legacy `severityLevel` value coming from the API:
//   "1" => Critical / Error, "2" => Warning, "3" => Information.
export const NOTI_EVENT_SEVERITIES = [
  { id: '1', labelId: 'notiEventLegendCritical' },
  { id: '2', labelId: 'notiEventLegendWarning' },
  { id: '3', labelId: 'notiEventLegendInfo' }
];
